import { useCallback, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { useAuth } from '@/hooks/useAuth';
import { useChatUnreadCountQuery } from '@/hooks/useChat';
import { CHAT_UNREAD_BADGE_MAXIMUM } from '@/constants/chat';
import { cn } from '@/lib/utils';
import { selectVisibleNavigationItems } from './navigationItems';
import { PractitionerMobileMoreMenu } from './PractitionerMobileMoreMenu';

const MOBILE_PRIMARY_ITEM_IDS = ['dashboard', 'clients', 'chat', 'settings'];

function buildUnreadBadgeLabel(unreadCount: number): string {
  return unreadCount > CHAT_UNREAD_BADGE_MAXIMUM
    ? String(CHAT_UNREAD_BADGE_MAXIMUM) + '+'
    : String(unreadCount);
}

export function PractitionerMobileNav() {
  const { t } = useTranslation(['onboarding', 'chat']);
  const { enabledModules, activeWorkspace, isElevatedWorkspaceAccess } = useAuth();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const closeMoreMenu = useCallback(function closeMoreMenu() {
    setIsMoreMenuOpen(false);
  }, []);
  const chatUnreadCountQuery = useChatUnreadCountQuery('practitioner');
  const chatUnreadCount =
    chatUnreadCountQuery.data === undefined ? 0 : chatUnreadCountQuery.data;
  const isOwner =
    (activeWorkspace !== null && activeWorkspace.isOwner === true) || isElevatedWorkspaceAccess;
  const visibleItems = selectVisibleNavigationItems(enabledModules, isOwner);
  const items = visibleItems.filter(function isPrimaryItem(item) {
    return MOBILE_PRIMARY_ITEM_IDS.includes(item.id);
  });
  const moreItems = visibleItems.filter(function isMoreItem(item) {
    return MOBILE_PRIMARY_ITEM_IDS.includes(item.id) === false && item.route !== null;
  });

  return (
    <>
      {isMoreMenuOpen === true ? (
        <PractitionerMobileMoreMenu items={moreItems} onClose={closeMoreMenu} />
      ) : null}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-zoowhite border-t border-zooborder flex">
        {items.map(function renderItem(item) {
          if (item.route === null) {
            return (
              <div
                key={item.id}
                className="flex-1 py-2.5 flex flex-col items-center gap-0.5 text-[10px] font-medium text-zoodisabledtext"
              >
                <Icon name={item.iconName} className="text-lg" />
                <span className="w-full truncate text-center">{t(item.labelKey)}</span>
              </div>
            );
          }
          return (
            <NavLink
              key={item.id}
              to={item.route}
              end
              className={function resolveClassName({ isActive }) {
                return cn(
                  'flex-1 py-2.5 flex flex-col items-center gap-0.5 text-[10px] font-medium transition-colors',
                  isActive === true ? 'text-zooprimary' : 'text-zoomuted'
                );
              }}
            >
              <span className="relative">
                <Icon name={item.iconName} className="text-lg" />
                {item.unreadBadge === true && chatUnreadCount > 0 ? (
                  <span
                    aria-label={t('chat:nav.unreadBadgeAriaLabel', { count: chatUnreadCount })}
                    className="absolute -top-1 -right-2 min-w-[14px] h-[14px] px-0.5 flex items-center justify-center rounded-full bg-zooprimary text-zoowhite text-[8px] font-bold"
                  >
                    {buildUnreadBadgeLabel(chatUnreadCount)}
                  </span>
                ) : null}
              </span>
              <span className="w-full truncate text-center">{t(item.labelKey)}</span>
            </NavLink>
          );
        })}
        {moreItems.length > 0 ? (
          <button
            type="button"
            aria-expanded={isMoreMenuOpen}
            onClick={function toggleMoreMenu() {
              setIsMoreMenuOpen(isMoreMenuOpen === false);
            }}
            className={cn(
              'flex-1 py-2.5 flex flex-col items-center gap-0.5 text-[10px] font-medium transition-colors',
              isMoreMenuOpen === true ? 'text-zooprimary' : 'text-zoomuted'
            )}
          >
            <Icon name="MoreHorizontal" className="text-lg" />
            <span className="w-full truncate text-center">{t('nav.more')}</span>
          </button>
        ) : null}
      </nav>
    </>
  );
}
